import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { Divider } from 'primereact/divider';
import { Editor } from 'primereact/editor';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import { TabMenu } from 'primereact/tabmenu';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useEmployee } from '../../context/employee';
import { useSite } from '../../context/site';
import { GetEditorFormats } from '../../shared';
import ChildCreate from '../child/create';
import ChildUpdate from '../child/update';
import DateStringCellTemplate from '../date-string-template';
import EmployeeDropdown from '../employee-dropdown';
import GenderCellTemplate from '../gender-cell-template';
import GenderDropdown from '../gender-dropdown';
import LanguageDropdown from '../language-dropdown';
import RelationshipDropdown from '../relationship-dropdown';
import StateDropdown from '../state-dropdown';

function ParentUpdate({ open, data, onClose, parentId }) {
    const parentRef = useRef(null);

    const { employees } = useEmployee();
    const { UpdateParent, ReadOneParent } = useSite();

    const [activeIndex, setActiveIndex] = useState(0);

    const [active, setActive] = useState(true);
    const [ccrr, setCcrr] = useState(false);
    const [sponsorJ2TC, setSponsorJ2TC] = useState(false);
    const [nameFirst, setNameFirst] = useState('');
    const [nameLast, setNameLast] = useState('');
    const [cellPhone, setCellPhone] = useState('');
    const [email, setEmail] = useState('');
    const [streetAddress1, setStreetAddress1] = useState('');
    const [streetAddress2, setStreetAddress2] = useState('');
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [zipCode, setZipCode] = useState('');

    const [language, setLanguage] = useState('');
    const [gender, setGender] = useState('');
    const [relationship, setRelationship] = useState('');
    const [workPhone, setWorkPhone] = useState('');
    const [homePhone, setHomePhone] = useState('');
    const [addedBy, setAddedBy] = useState('');
    const [dateAdded, setDateAdded] = useState(new Date());
    const [spouseNameFirst, setSpouseNameFirst] = useState('');
    const [spouseNameLast, setSpouseNameLast] = useState('');
    const [notes, setNotes] = useState('');

    const [children, setChildren] = useState([]);

    const [processing, setProcessing] = useState(false);
    const [processingError, setProcessingError] = useState('');

    const [loadingParent, setLoadingParent] = useState(true);

    const tabItems = [{ label: 'Basic' }, { label: 'Other' }, { label: 'Children' }];

    const buttonDisabled = () => {
        if (!parentRef.current) {
            return true;
        }

        if (active !== parentRef.current.active) {
            return false;
        }

        if (ccrr !== parentRef.current.ccrr) {
            return false;
        }

        if (sponsorJ2TC !== parentRef.current.sponsorJ2TC) {
            return false;
        }

        if (nameFirst !== parentRef.current.nameFirst) {
            if (!nameFirst) {
                return true;
            }

            return false;
        }

        if (nameLast !== parentRef.current.nameLast) {
            if (!nameLast) {
                return true;
            }

            return false;
        }

        if (cellPhone !== parentRef.current.cellPhone) {
            if (cellPhone && cellPhone.length !== 10) {
                return true;
            }

            return false;
        }

        if (email !== parentRef.current.email) {
            return false;
        }

        if (streetAddress1 !== parentRef.current.streetAddress1) {
            return false;
        }

        if (streetAddress2 !== parentRef.current.streetAddress2) {
            return false;
        }

        if (city !== parentRef.current.city) {
            return false;
        }

        if (state !== parentRef.current.state._id) {
            return false;
        }

        if (zipCode !== parentRef.current.zipCode) {
            if (zipCode && zipCode.length !== 5) {
                return true;
            }

            return false;
        }

        if (language !== parentRef.current.language._id) {
            return false;
        }

        if (gender !== parentRef.current.gender._id) {
            return false;
        }

        if (relationship !== parentRef.current.relationship._id) {
            return false;
        }

        if (homePhone !== parentRef.current.homePhone) {
            if (homePhone && homePhone.length !== 10) {
                return true;
            }

            return false;
        }

        if (workPhone !== parentRef.current.workPhone) {
            if (workPhone && workPhone.length !== 10) {
                return true;
            }

            return false;
        }

        if (addedBy !== parentRef.current.addedBy._id) {
            return false;
        }

        if (dateAdded.toISOString() !== parentRef.current.dateAdded) {
            return false;
        }

        if (spouseNameFirst !== parentRef.current.spouseNameFirst) {
            return false;
        }

        if (spouseNameLast !== parentRef.current.spouseNameLast) {
            return false;
        }

        if (notes !== parentRef.current.notes) {
            return false;
        }

        // Find children that were added or updated
        const creates = [...children.filter((c) => !c._id)];
        const updates = [];

        for (const childNew of children.filter((c) => !!c._id)) {
            const childCurrent = parentRef.current.children.find((c) => c._id === childNew._id);

            for (const field of ['active', 'nameFirst', 'nameLast', 'gender', 'dob', 'notes']) {
                const shouldAdd = childCurrent[field] !== childNew[field];

                if (shouldAdd) {
                    updates.push(childNew);
                }
            }
        }

        if (creates.length > 0 || updates.length > 0) {
            return false;
        }

        return true;
    };

    const onTabChange = ({ index }) => {
        setActiveIndex(index);
    };

    const reset = () => {
        setActiveIndex(0);

        setActive(true);
        setCcrr(false);
        setSponsorJ2TC(false);
        setNameFirst('');
        setNameLast('');
        setCellPhone('');
        setEmail('');
        setStreetAddress1('');
        setStreetAddress2('');
        setCity('');
        setState('');
        setZipCode('');

        setLanguage('');
        setGender('');
        setRelationship('');
        setHomePhone('');
        setWorkPhone('');
        setAddedBy('');
        setDateAdded(new Date());
        setSpouseNameFirst('');
        setSpouseNameLast('');
        setNotes('');

        setChildren([]);

        setProcessing(false);
        setProcessingError('');
    };

    const onCancel = () => {
        reset();
        onClose({ status: 499, message: 'Canceled' });
    };

    const onUpdate = () => {
        setProcessing(true);

        const updateData = {
            parent: {
                _id: parentRef.current._id
            },
            children: {
                creates: [],
                updates: []
            }
        };

        if (active !== parentRef.current.active) {
            updateData.parent.active = active;
        }

        if (ccrr !== parentRef.current.ccrr) {
            updateData.parent.ccrr = ccrr;
        }

        if (sponsorJ2TC !== parentRef.current.sponsorJ2TC) {
            updateData.parent.sponsorJ2TC = sponsorJ2TC;
        }

        if (nameFirst !== parentRef.current.nameFirst) {
            updateData.parent.nameFirst = nameFirst;
        }

        if (nameLast !== parentRef.current.nameLast) {
            updateData.parent.nameLast = nameLast;
        }

        if (cellPhone !== parentRef.current.cellPhone) {
            updateData.parent.cellPhone = cellPhone;
        }

        if (email !== parentRef.current.email) {
            updateData.parent.email = email;
        }

        if (streetAddress1 !== parentRef.current.streetAddress1) {
            updateData.parent.streetAddress1 = streetAddress1;
        }

        if (streetAddress2 !== parentRef.current.streetAddress2) {
            updateData.parent.streetAddress2 = streetAddress2;
        }

        if (city !== parentRef.current.city) {
            updateData.parent.city = city;
        }

        if (state !== parentRef.current.state._id) {
            updateData.parent.state = state;
        }

        if (zipCode !== parentRef.current.zipCode) {
            updateData.parent.zipCode = zipCode;
        }

        if (homePhone !== parentRef.current.homePhone) {
            updateData.parent.homePhone = homePhone;
        }

        if (workPhone !== parentRef.current.workPhone) {
            updateData.parent.workPhone = workPhone;
        }

        if (spouseNameFirst !== parentRef.current.spouseNameFirst) {
            updateData.parent.spouseNameFirst = spouseNameFirst;
        }

        if (spouseNameLast !== parentRef.current.spouseNameLast) {
            updateData.parent.spouseNameLast = spouseNameLast;
        }

        if (notes !== parentRef.current.notes) {
            updateData.parent.notes = notes;
        }

        if (language !== parentRef.current.language._id) {
            updateData.parent.language = language;
        }

        if (gender !== parentRef.current.gender._id) {
            updateData.parent.gender = gender;
        }

        if (relationship !== parentRef.current.relationship._id) {
            updateData.parent.relationship = relationship;
        }

        if (dateAdded.toISOString() !== parentRef.current.dateAdded) {
            updateData.parent.dateAdded = dateAdded;
        }

        if (addedBy !== parentRef.current.addedBy._id) {
            updateData.parent.addedBy = addedBy;
        }

        // Find children that were added or updated
        const creates = [...children.filter((c) => !c._id)];

        for (const _create of creates) {
            _create.parent = parentRef.current._id;

            updateData.children.creates.push(_create);
        }

        const updates = [];

        for (const childNew of children.filter((c) => !!c._id)) {
            const childCurrent = parentRef.current.children.find((c) => c._id === childNew._id);

            for (const field of ['active', 'nameFirst', 'nameLast', 'gender', 'dob', 'notes']) {
                const shouldAdd = childCurrent[field] !== childNew[field];

                if (shouldAdd) {
                    updates.push(childNew);
                }
            }
        }

        for (const _update of updates) {
            updateData.children.updates.push(_update);
        }

        UpdateParent(updateData, (res) => {
            if (res.status !== 200) {
                setProcessingError(res.message);
                setProcessing(false);
            } else {
                reset();
                onClose(res);
            }
        });
    };

    const FooterContent = (
        <div>
            <Button
                label="Cancel"
                icon="pi pi-times"
                onClick={onCancel}
                className="p-button-text"
            />
            <Button
                label="Update"
                disabled={buttonDisabled()}
                icon="pi pi-check"
                onClick={onUpdate}
            />
        </div>
    );

    const setParent = (data) => {
        parentRef.current = data;

        setActive(data.active);
        setCcrr(data.ccrr);
        setSponsorJ2TC(data.sponsorJ2TC);
        setNameFirst(data.nameFirst);
        setNameLast(data.nameLast);
        setHomePhone(data.homePhone);
        setCellPhone(data.cellPhone);
        setEmail(data.email);
        setStreetAddress1(data.streetAddress1);
        setStreetAddress2(data.streetAddress2);
        setCity(data.city);
        setState(data?.state?._id ? data.state._id : '');
        setZipCode(data.zipCode);

        setLanguage(data?.language?._id ? data.language._id : '');
        setGender(data?.gender?._id ? data.gender._id : '');
        setRelationship(data?.relationship?._id ? data.relationship._id : '');
        setHomePhone(data.homePhone);
        setWorkPhone(data.workPhone);
        setAddedBy(data?.addedBy?._id ? data.addedBy._id : '');
        setDateAdded(new Date(data.dateAdded));
        setSpouseNameFirst(data.spouseNameFirst);
        setSpouseNameLast(data.spouseNameLast);
        setNotes(data.notes);

        setChildren([...data.children]);
    };

    useEffect(() => {
        if (!parentId) {
            return;
        }

        if (!loadingParent) {
            return;
        }

        ReadOneParent({ parentId }, (res) => {
            setLoadingParent(false);
            setParent(res.data.parent);
        });
    }, [parentId, loadingParent, ReadOneParent]);

    // On load set values
    useEffect(() => {
        if (Object.keys(data).length === 0) {
            return;
        }

        setParent(data);
        setLoadingParent(false);
    }, [data]);

    return (
        <Dialog
            header="Update Parent"
            visible={open}
            onHide={onCancel}
            footer={FooterContent}
            className="w-9"
        >
            <TabMenu model={tabItems} activeIndex={activeIndex} onTabChange={onTabChange} />

            <ErrorMessage
                error={processingError}
                onClick={() => {
                    setProcessingError('');
                }}
            />

            <ProcessingMessage processing={processing} />

            <LoadingMessage loading={loadingParent} />

            {activeIndex !== 0 || processing || loadingParent ? null : (
                <TabBasic
                    active={active}
                    setActive={setActive}
                    ccrr={ccrr}
                    setCcrr={setCcrr}
                    sponsorJ2TC={sponsorJ2TC}
                    setSponsorJ2TC={setSponsorJ2TC}
                    nameFirst={nameFirst}
                    setNameFirst={setNameFirst}
                    nameLast={nameLast}
                    setNameLast={setNameLast}
                    cellPhone={cellPhone}
                    setCellPhone={setCellPhone}
                    email={email}
                    setEmail={setEmail}
                    streetAddress1={streetAddress1}
                    setStreetAddress1={setStreetAddress1}
                    streetAddress2={streetAddress2}
                    setStreetAddress2={setStreetAddress2}
                    city={city}
                    setCity={setCity}
                    state={state}
                    setState={setState}
                    zipCode={zipCode}
                    setZipCode={setZipCode}
                />
            )}

            {activeIndex !== 1 || processing || loadingParent ? null : (
                <TabOther
                    language={language}
                    setLanguage={setLanguage}
                    gender={gender}
                    setGender={setGender}
                    relationship={relationship}
                    setRelationship={setRelationship}
                    homePhone={homePhone}
                    setHomePhone={setHomePhone}
                    workPhone={workPhone}
                    setWorkPhone={setWorkPhone}
                    dateAdded={dateAdded}
                    setDateAdded={setDateAdded}
                    addedBy={addedBy}
                    setAddedBy={setAddedBy}
                    employees={employees}
                    notes={notes}
                    setNotes={setNotes}
                    spouseNameFirst={spouseNameFirst}
                    setSpouseNameFirst={setSpouseNameFirst}
                    spouseNameLast={spouseNameLast}
                    setSpouseNameLast={setSpouseNameLast}
                />
            )}

            {activeIndex !== 2 || processing || loadingParent ? null : (
                <TabChildren children={children} setChildren={setChildren} />
            )}
        </Dialog>
    );
}

function ErrorMessage({ error, onClick }) {
    if (!error) {
        return null;
    }

    return <Message severity="error" content={error} onClick={onClick} />;
}

function ProcessingMessage({ processing }) {
    if (!processing) {
        return null;
    }

    return (
        <>
            <p>Updating Parent...</p>

            <ProgressSpinner />
        </>
    );
}

function LoadingMessage({ loading }) {
    if (!loading) {
        return null;
    }

    return (
        <>
            <p>Loading Parent...</p>

            <ProgressSpinner />
        </>
    );
}

function TabBasic({
    active,
    setActive,
    ccrr,
    setCcrr,
    sponsorJ2TC,
    setSponsorJ2TC,
    nameFirst,
    setNameFirst,
    nameLast,
    setNameLast,
    cellPhone,
    setCellPhone,
    email,
    setEmail,
    streetAddress1,
    setStreetAddress1,
    streetAddress2,
    setStreetAddress2,
    city,
    setCity,
    state,
    setState,
    zipCode,
    setZipCode
}) {
    const getCellPhoneClassname = useMemo(() => {
        const count = cellPhone.length;
        const res = count === 0 || count === 10 ? '' : 'p-invalid';

        return res;
    }, [cellPhone]);

    return (
        <div className="mt-2">
            <div className="card flex flex-wrap gap-3 p-fluid">
                <div className="flex-auto">
                    <label htmlFor="activeId" className="font-bold block mb-2">
                        Active
                    </label>

                    <InputSwitch
                        inputId="activeId"
                        checked={active}
                        onChange={(e) => {
                            setActive(e.target.value);
                        }}
                    />
                </div>

                <div className="flex-auto">
                    <label htmlFor="ccrrId" className="font-bold block mb-2">
                        CCRR
                    </label>

                    <InputSwitch
                        inputId="ccrrId"
                        checked={ccrr}
                        onChange={(e) => {
                            setCcrr(e.target.value);
                        }}
                    />
                </div>

                <div className="flex-auto">
                    <label htmlFor="sponsorJ2TCId" className="font-bold block mb-2">
                        Sponsor J2TC
                    </label>

                    <InputSwitch
                        inputId="sponsorJ2TCId"
                        checked={sponsorJ2TC}
                        onChange={(e) => {
                            setSponsorJ2TC(e.target.value);
                        }}
                    />
                </div>
            </div>

            <div className="card flex flex-wrap gap-3 p-fluid">
                <div className="flex-auto">
                    <label htmlFor="nameFirstId" className="font-bold block mb-2">
                        First Name
                    </label>

                    <InputText
                        id="nameFirstId"
                        value={nameFirst}
                        keyfilter={/^[a-zA-Z ]*$/}
                        maxLength={24}
                        onChange={(e) => {
                            setNameFirst(e.target.value);
                        }}
                    />
                </div>

                <div className="flex-auto">
                    <label htmlFor="nameLastId" className="font-bold block mb-2">
                        Last Name
                    </label>

                    <InputText
                        id="nameLastId"
                        value={nameLast}
                        keyfilter={/^[a-zA-Z ]*$/}
                        maxLength={24}
                        onChange={(e) => {
                            setNameLast(e.target.value);
                        }}
                    />
                </div>
            </div>

            <div className="card flex flex-wrap gap-3 p-fluid">
                <div className="flex-auto">
                    <label htmlFor="cellPhoneId" className="font-bold block mb-2">
                        Cell Phone
                    </label>

                    <InputText
                        id="cellPhoneId"
                        value={cellPhone}
                        keyfilter={/^[0-9]*$/}
                        minLength={10}
                        className={getCellPhoneClassname}
                        maxLength={10}
                        onChange={(e) => {
                            setCellPhone(e.target.value);
                        }}
                    />

                    {cellPhone.length === 0 || cellPhone.length === 10 ? null : (
                        <small id="ecPhoneId-help">
                            The phone number should be exactly 10 digits
                        </small>
                    )}
                </div>

                <div className="flex-auto">
                    <label htmlFor="emailId" className="font-bold block mb-2">
                        Email
                    </label>

                    <InputText
                        id="emailId"
                        value={email}
                        maxLength={48}
                        onChange={(e) => {
                            setEmail(e.target.value);
                        }}
                    />
                </div>
            </div>

            <div className="card flex flex-wrap gap-3 p-fluid">
                <div className="flex-auto">
                    <label htmlFor="streetAddress1Id" className="font-bold block mb-2">
                        Address Line 1
                    </label>

                    <InputText
                        id="streetAddress1Id"
                        value={streetAddress1}
                        keyfilter={/^[a-zA-Z0-9 ]*$/}
                        maxLength={42}
                        onChange={(e) => {
                            setStreetAddress1(e.target.value);
                        }}
                    />
                </div>
            </div>

            <div className="card flex flex-wrap gap-3 p-fluid">
                <div className="flex-auto">
                    <label htmlFor="streetAddress2Id" className="font-bold block mb-2">
                        Address Line 2
                    </label>

                    <InputText
                        id="streetAddress2Id"
                        value={streetAddress2}
                        keyfilter={/^[a-zA-Z0-9 ]*$/}
                        maxLength={42}
                        onChange={(e) => {
                            setStreetAddress2(e.target.value);
                        }}
                    />
                </div>
            </div>

            <div className="card flex flex-wrap gap-3 p-fluid">
                <div className="flex-auto">
                    <label htmlFor="cityId" className="font-bold block mb-2">
                        City
                    </label>

                    <InputText
                        id="cityId"
                        value={city}
                        keyfilter={/^[a-zA-Z ]*$/}
                        maxLength={42}
                        onChange={(e) => {
                            setCity(e.target.value);
                        }}
                    />
                </div>

                <div className="flex-auto">
                    <label htmlFor="stateId" className="font-bold block mb-2">
                        State
                    </label>

                    <StateDropdown
                        stateId={state}
                        onChange={(stateId) => {
                            setState(stateId);
                        }}
                    />
                </div>

                <div className="flex-auto">
                    <label htmlFor="zipId" className="font-bold block mb-2">
                        Zip Code
                    </label>

                    <InputText
                        id="zipId"
                        value={zipCode}
                        keyfilter={/^[0-9]*$/}
                        minLength={5}
                        maxLength={5}
                        onChange={(e) => {
                            setZipCode(e.target.value);
                        }}
                    />
                </div>
            </div>
        </div>
    );
}

function TabOther({
    language,
    setLanguage,
    languages,
    gender,
    setGender,
    relationship,
    setRelationship,
    homePhone,
    setHomePhone,
    workPhone,
    setWorkPhone,
    dateAdded,
    setDateAdded,
    addedBy,
    setAddedBy,
    employees,
    notes,
    setNotes,
    spouseNameFirst,
    setSpouseNameFirst,
    spouseNameLast,
    setSpouseNameLast
}) {
    const getHomePhoneClassname = useMemo(() => {
        const count = `${homePhone}`.length;
        const res = count === 0 || count === 10 ? '' : 'p-invalid';

        return res;
    }, [homePhone]);

    const getWorkPhoneClassname = useMemo(() => {
        const count = workPhone.length;
        const res = count === 0 || count === 10 ? '' : 'p-invalid';

        return res;
    }, [workPhone]);

    return (
        <div className="mt-2">
            <div className="card flex flex-wrap gap-3 p-fluid">
                <div className="flex-auto">
                    <label htmlFor="languageId" className="font-bold block mb-2">
                        Language
                    </label>

                    <LanguageDropdown
                        languageId={language}
                        onChange={(languageId) => {
                            setLanguage(languageId);
                        }}
                    />
                </div>

                <div className="flex-auto">
                    <label htmlFor="genderId" className="font-bold block mb-2">
                        Gender
                    </label>

                    <GenderDropdown
                        genderId={gender}
                        onChange={(genderId) => {
                            setGender(genderId);
                        }}
                    />
                </div>

                <div className="flex-auto">
                    <label htmlFor="relationshipId" className="font-bold block mb-2">
                        Relationship
                    </label>

                    <RelationshipDropdown
                        relationshipId={relationship}
                        onChange={(relationshipId) => {
                            setRelationship(relationshipId);
                        }}
                    />
                </div>
            </div>

            <div className="card flex flex-wrap gap-3 p-fluid">
                <div className="flex-auto">
                    <label htmlFor="homePhoneId" className="font-bold block mb-2">
                        Home Phone
                    </label>

                    <InputText
                        id="homePhoneId"
                        value={homePhone}
                        keyfilter={/^[0-9]*$/}
                        minLength={10}
                        className={getHomePhoneClassname}
                        maxLength={10}
                        onChange={(e) => {
                            setHomePhone(e.target.value);
                        }}
                    />

                    {homePhone.length === 0 || homePhone.length === 10 ? null : (
                        <small id="ecPhoneId-help">
                            The phone number should be exactly 10 digits
                        </small>
                    )}
                </div>

                <div className="flex-auto">
                    <label htmlFor="workPhoneId" className="font-bold block mb-2">
                        Work Phone
                    </label>

                    <InputText
                        id="workPhoneId"
                        value={workPhone}
                        keyfilter={/^[0-9]*$/}
                        minLength={10}
                        className={getWorkPhoneClassname}
                        maxLength={10}
                        onChange={(e) => {
                            setWorkPhone(e.target.value);
                        }}
                    />

                    {workPhone.length === 0 || workPhone.length === 10 ? null : (
                        <small id="ecPhoneId-help">
                            The phone number should be exactly 10 digits
                        </small>
                    )}
                </div>
            </div>

            <div className="card flex flex-wrap gap-3 p-fluid">
                <div className="flex-auto">
                    <label htmlFor="dateAddedId" className="font-bold block mb-2">
                        Date Added
                    </label>

                    <Calendar
                        inputId="dateAddedId"
                        value={dateAdded}
                        onChange={(e) => setDateAdded(e.value)}
                        showIcon
                        showButtonBar
                    />
                </div>

                <div className="flex-auto">
                    <label htmlFor="addedById" className="font-bold block mb-2">
                        Added By
                    </label>

                    <EmployeeDropdown
                        employeeId={addedBy}
                        onChange={(employeeId) => setAddedBy(employeeId)}
                    />
                </div>
            </div>

            <div className="card flex flex-wrap gap-3 p-fluid">
                <div className="flex-auto">
                    <label htmlFor="spouseFirstNameId" className="font-bold block mb-2">
                        Spouse First Name
                    </label>

                    <InputText
                        id="spouseFirstNameId"
                        value={spouseNameFirst}
                        keyfilter={/^[a-zA-Z ]*$/}
                        maxLength={24}
                        onChange={(e) => {
                            setSpouseNameFirst(e.target.value);
                        }}
                    />
                </div>

                <div className="flex-auto">
                    <label htmlFor="spouseLastNameId" className="font-bold block mb-2">
                        Spouse Last Name
                    </label>

                    <InputText
                        id="spouseLastNameId"
                        value={spouseNameLast}
                        keyfilter={/^[a-zA-Z ]*$/}
                        maxLength={24}
                        onChange={(e) => {
                            setSpouseNameLast(e.target.value);
                        }}
                    />
                </div>
            </div>

            <div className="card flex flex-wrap gap-3 p-fluid">
                <div className="flex-auto">
                    <label htmlFor="notesId" className="font-bold block mb-2">
                        Notes
                    </label>

                    <Editor
                        id="notesId"
                        value={notes}
                        headerTemplate={<GetEditorFormats />}
                        onTextChange={(e) => setNotes(e.htmlValue)}
                        style={{ height: '320px' }}
                    />
                </div>
            </div>
        </div>
    );
}

function TabChildren({ children, setChildren, genders }) {
    const [openCreate, setOpenCreate] = useState(false);
    const [openUpdate, setOpenUpdate] = useState(false);
    const [updateData, setUpdateData] = useState({});

    const onClickCreate = () => {
        setOpenCreate(true);
    };

    const onCloseCreate = (child) => {
        if (child) {
            setChildren((prev) => {
                return [...prev, child];
            });
        }

        setOpenCreate(false);
    };

    const onCloseUpdate = (data) => {
        do {
            if (!data) {
                break;
            }

            const { child, index } = data;

            if (!child) {
                break;
            }

            setChildren((prev) => {
                const _children = [
                    ...prev.map((p, i) => {
                        if (p._id === child?._id || i === index) {
                            return {
                                ...p,
                                ...child
                            };
                        }

                        return p;
                    })
                ];

                return _children;
            });
        } while (0);

        setUpdateData({});
        setOpenUpdate(false);
    };

    const toggleActivateChild = (childIndex, child) => {
        setChildren((prev) => {
            if (!child?._id) {
                return [...prev.filter((p, i) => i !== childIndex)];
            }

            return [
                ...prev.map((p, i) => {
                    if (p._id === child._id) {
                        return {
                            ...p,
                            active: !p.active
                        };
                    }

                    return p;
                })
            ];
        });
    };

    const rowClass = (child) => {
        return {
            rowInactive: !child.active
        };
    };

    const onRowClick = ({ data, index }) => {
        setUpdateData({ child: data, index });
        setOpenUpdate(true);
    };

    const actionTitle = (child) => {
        if (!child?._id) {
            return 'Delete';
        }

        return child.active ? 'Deactivate' : 'Activate';
    };

    const ActionsDisplay = (child, { rowIndex }) => {
        return (
            <Button
                icon="pi pi-user"
                className="ml-1"
                size="small"
                severity={child?._id ? 'secondary' : 'danger'}
                rounded
                title={actionTitle(child)}
                onClick={() => {
                    toggleActivateChild(rowIndex, child);
                }}
            />
        );
    };

    const NotesDisplay = (child) => {
        if (!child.notes) {
            return '';
        }

        return <span dangerouslySetInnerHTML={{ __html: child.notes }}></span>;
    };

    return (
        <div>
            <Divider />

            <Button icon="pi pi-plus" rounded severity="success" onClick={onClickCreate} />

            <Divider />

            <DataTable rowClassName={rowClass} value={children} onRowClick={onRowClick}>
                <Column field="nameFirst" header="First Name"></Column>

                <Column field="nameLast" header="Last Name"></Column>

                <Column
                    field="gender"
                    header="Gender"
                    body={(data) => {
                        return <GenderCellTemplate child={data} />;
                    }}
                ></Column>

                <Column
                    field="dob"
                    header="Date of Birth"
                    body={(data) => {
                        return <DateStringCellTemplate child={data} />;
                    }}
                ></Column>

                <Column body={NotesDisplay} header="Notes"></Column>

                <Column
                    header="Actions"
                    bodyStyle={{ width: '150px' }}
                    body={ActionsDisplay}
                ></Column>
            </DataTable>

            {!openCreate ? null : <ChildCreate open={openCreate} onClose={onCloseCreate} />}

            {!openUpdate ? null : (
                <ChildUpdate open={openUpdate} data={updateData} onClose={onCloseUpdate} />
            )}
        </div>
    );
}

export default ParentUpdate;
